import { CLASSES, type DailyEntry } from '@xox/shared'
import { Avatar } from '../components/Avatar'
import { Card } from '../components/ui'

interface Props {
  entries: readonly DailyEntry[]
  me: string | null
  /** Shown while the list is still loading or empty. */
  emptyText?: string
}

/** The day's ranking: who got deepest, with the most points. */
export function DailyBoard({
  entries,
  me,
  emptyText = 'Nobody has entered today. Be first.',
}: Props) {
  return (
    <Card className="overflow-x-auto p-0">
      {entries.length === 0 && <p className="p-5 text-slate-500">{emptyText}</p>}
      {entries.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Player</th>
              <th className="px-4 py-3 text-right">Floor</th>
              <th className="px-4 py-3 text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const isMe = e.player.id === me
              return (
                <tr
                  key={e.player.id}
                  className={[
                    'border-t border-slate-100 dark:border-slate-800',
                    isMe ? 'bg-sky-50 font-semibold dark:bg-sky-950/40' : '',
                  ].join(' ')}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{e.rank}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <Avatar
                        id={e.player.id}
                        avatar={e.player.avatar}
                        color={e.player.color}
                        size="sm"
                      />
                      <span>
                        {e.player.nickname}
                        {isMe && <span className="ml-1 text-xs text-sky-600">(you)</span>}
                        <span className="block text-[10px] font-normal text-slate-500">
                          {CLASSES[e.cls].name} ·{' '}
                          {e.status === 'active'
                            ? 'delving…'
                            : e.status === 'won'
                              ? 'cleared!'
                              : e.status}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{e.floor}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold">{e.score}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}
